import React, { useCallback, useState } from 'react';
import {
  Button,
  Box,
  TextField,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import styles from './TaxInvoices.module.css';
import { useAuthUser } from 'react-auth-kit';
import { useSelector } from 'react-redux';
import AutoCompleteField from '../../../../../components/AutoCompleteField/AutoCompleteField';
import AutoCompleteFilter from '../../../Reports/components/Filters/AutoCompleteFilter';
import { getSubDomainAccessStatus } from '../../../../../components/RolePermissionAccess/RolePermissionAccess';
import { useCountries } from 'use-react-countries';

const vatRegexByCountry: Record<string, RegExp> = {
  Qatar: /^[0-9]{6}$/,
  'United Arab Emirates': /^[1-9][0-9]{14}$/,
};
const vatPattern = /^[a-zA-Z0-9]{15}$/;

function EditGst(props: any) {
  const { onCancelEdit, UpdateStudentInvoiceSettings, invoiceSettings } = props;
  console.log('invoiceSettings', invoiceSettings);
  const auth = useAuthUser();
  const authUser = auth();
  const { countries } = useCountries();
  const { userById } = useSelector((state: any) => state.userDetails);
  const { invoiceSettingUpdateLoading } = useSelector(
    (state: any) => state.appSettings
  );
  const [gstin, setGstin] = useState('');
  const [vat, setVat] = useState('');
  const [gstError, setGstError] = React.useState<boolean>(false);
  const [vatError, setVatError] = useState<boolean>(false);
  const gstNumberRegex =
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;

  const hasAllBranchAccess = getSubDomainAccessStatus(
    'GLOBAL_PERMISSIONS',
    'BRANCH_ACCESS',
    'ALL_BRANCHES',
    userById?.rolePermission
  );
  const { branchList } = useSelector((state: any) => state.branch);
  const filteredBranchList = hasAllBranchAccess
    ? branchList
    : branchList?.filter((branch: any) => branch.id === userById?.branchId);

  const [country, setCountry] = React.useState<string>('');
  const [validStateCountry, setValidStateCountry] =
    React.useState<boolean>(true);

  const [selectedBranches, setSelectedBranches] = useState<any>(null);
  const [validStateBranch, setValidStateBranch] = React.useState<boolean>(true);

  const filteredBranches = country
    ? filteredBranchList?.filter((branch: any) => branch.country === country)
    : [];

  const countrySelected = countries
    ? countries?.find(
        (item: any) => item.name?.toLowerCase() === country?.toLowerCase()
      )
    : '';
  const onCountryChange = React.useCallback(
    (event: object, value: any | null) => {
      if (value !== null) {
        setCountry(value?.name);
        setValidStateCountry(true);
      } else {
        setCountry('');
        setValidStateCountry(false);
      }
      setSelectedBranches(null);
    },
    []
  );

  const onBranchChange = React.useCallback(
    (event: object, value: any | null) => {
      if (value !== null) {
        setSelectedBranches(value);
        setValidStateBranch(true);
      } else {
        setSelectedBranches(null);
        setValidStateBranch(false);
      }
    },
    []
  );

  const handleCancel = () => {
    onCancelEdit();
  };
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = event.target;
      const trimmedValue = value.toUpperCase();

      if (id === 'gstNumber') {
        setGstin(trimmedValue);
        setGstError(
          trimmedValue.length > 0 && !gstNumberRegex.test(trimmedValue)
        );
      } else if (id === 'vatNumber') {
        setVat(trimmedValue);
        setVatError(trimmedValue.length > 0 && !vatPattern.test(trimmedValue));
      }
    },
    []
  );

  const handleSaveClick = async (event: any) => {
    event.preventDefault();

    try {
      const branchData =
        selectedBranches?.map((branch: any) => ({
          branchId: branch?.id,
          branchName: branch.branchName,
        })) || [];
      const body: any = {
        instituteId: authUser?.institute?.[0]?.instituteId,
        nextNumber: invoiceSettings?.nextNumber,
        prefix: invoiceSettings?.prefix,
        isStudentTaxInvoiceEnabled: true,
        taxInvoiceCountry: country,
        id: invoiceSettings?.id,
        branchData: branchData,
      };

      // Conditionally add either gstNumber or vatNumber based on the selection
      if (country === 'India') body.gstNumber = gstin;
      else body.vatNumber = vat;
      if (gstin || vat) UpdateStudentInvoiceSettings(body);
    } catch (error) {}
  };

  React.useEffect(() => {
    if (invoiceSettings && branchList?.length > 0) {
      setCountry(invoiceSettings?.taxInvoiceCountry);
      const savedBranches = filteredBranchList?.filter((item: any) =>
        invoiceSettings?.branchData?.some(
          (branch: any) => branch.branchId === item.id
        )
      );

      setSelectedBranches(savedBranches);
      if (invoiceSettings?.gstNumber) {
        setGstin(invoiceSettings?.gstNumber);
      } else {
        setVat(invoiceSettings?.vatNumber);
      }
    }
  }, [invoiceSettings, branchList]);

  return (
    <Box>
      <div className={styles.editGst}>
        Edit {country === 'India' ? 'GSTIN' : 'VAT'}
      </div>

      <div style={{ marginBottom: '15px' }}>
        <AutoCompleteField
          id="country"
          label="Select Country"
          options={countries?.filter((c: any) =>
            filteredBranchList?.some((b: any) => b.country === c.name)
          )}
          optionLabel={'name'}
          onChange={onCountryChange}
          value={countrySelected}
          disableClearable={country === '' ? true : false}
          required={true}
          validState={validStateCountry}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <AutoCompleteFilter
          id="branchId"
          label="Select Branch"
          options={filteredBranches}
          optionLabel={'branchName'}
          onChange={onBranchChange}
          value={selectedBranches}
          disableClearable={selectedBranches === null ? true : false}
          validState={true}
          multiSelect={true}
          width={'100%'}
          listType="BRANCH"
          required={true}
        />
        {!validStateBranch && (
          <FormHelperText sx={{ marginLeft: '8px' }} error>
            Field required
          </FormHelperText>
        )}
      </div>
      <div className={styles.gstIn}>
        {country === 'India' ? 'GSTIN' : 'VAT'}
        <span style={{ color: '#EC3939', fontWeight: '400' }}>*</span>
      </div>
      {country === 'India' ? (
        <TextField
          id="gstNumber"
          variant="outlined"
          fullWidth
          value={gstin}
          inputProps={{ maxLength: 15 }}
          error={gstError}
          helperText={gstError ? 'Invalid GST Number' : ''}
          onChange={handleChange}
          size="small"
          sx={{
            '& .MuiOutlinedInput-input': {
              padding: '11px',
            },
          }}
        />
      ) : (
        <TextField
          id="vatNumber"
          variant="outlined"
          fullWidth
          value={vat}
          inputProps={{ maxLength: 15 }}
          error={vatError}
          helperText={vatError ? 'Invalid VAT Number' : ''}
          onChange={handleChange}
          size="small"
          sx={{
            '& .MuiOutlinedInput-input': {
              padding: '11px',
            },
          }}
        />
      )}
      <Box sx={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
        <Button
          sx={{
            color: '#BFBFBF',
            border: '1px solid #BFBFBF',
            background: '#FFF',
            textTransform: 'capitalize',
          }}
          variant="outlined"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          sx={{ textTransform: 'capitalize' }}
          variant="contained"
          onClick={handleSaveClick}
          disabled={
            invoiceSettingUpdateLoading ||
            (country === 'India' ? !gstin : !vat) ||
            country === '' ||
            selectedBranches === null ||
            selectedBranches?.length === 0
          }
        >
          {invoiceSettingUpdateLoading ? (
            <CircularProgress size={20} color={'inherit'} />
          ) : (
            'Save'
          )}
        </Button>
      </Box>
    </Box>
  );
}
export default EditGst;
